import type { ComponentChildren } from "preact";
import type { PortfolioEntry } from "./types";
import { shortAddress, type WalletState } from "./wallet";

export function QyooWordmark() {
  return <span class="wordmark" aria-label="Qyoo">Qyoo<span aria-hidden="true">•</span></span>;
}

export function Header(props: {
  wallet: WalletState;
  network: string | null;
  production: boolean;
  developmentMint?: boolean;
  navigate(path: string): void;
}) {
  return (
    <header class="site-header">
      <button class="brand-button" onClick={() => props.navigate("/")} aria-label="Qyoo home">
        <QyooWordmark />
      </button>
      <nav aria-label="Main navigation">
        <button onClick={() => props.navigate("/demo")}>Try Qyoo</button>
        {props.developmentMint && <button onClick={() => props.navigate("/mint")}>Get a beta Qyoo</button>}
        <button onClick={() => props.navigate("/my-qyoos")}>My Qyoos</button>
      </nav>
      <div class="wallet-cluster">
        {props.network && <span class="network-badge">{props.network}</span>}
        {props.wallet.account
          ? <span class="wallet-pill" title={props.wallet.account}>{shortAddress(props.wallet.account)}</span>
          : <button class="button compact" disabled={props.wallet.busy} onClick={() => void props.wallet.connect()}>
              {props.wallet.busy ? "Connecting…" : "Connect wallet"}
            </button>}
      </div>
    </header>
  );
}

export function Layout(props: {
  children: ComponentChildren;
  wallet: WalletState;
  network: string | null;
  production: boolean;
  developmentMint?: boolean;
  navigate(path: string): void;
}) {
  return (
    <>
      <Header production={props.production} developmentMint={props.developmentMint ?? false} wallet={props.wallet} network={props.network} navigate={props.navigate} />
      <div class="beta-banner">{props.production ? "Qyoo for iPhone · Active development · Acquisition closed" : "Active public beta · iPhone / iOS 17+ · Sepolia test network · No monetary value"}</div>
      <main id="main">{props.children}</main>
      <footer>
        <QyooWordmark />
        <p>{props.production ? (props.network ? "Holders control issued Qyoos and their resolvers. Ethereum Mainnet records ownership; content can live elsewhere." : "Holders control issued Qyoos and their resolvers. Content can live elsewhere.") : "Holders control issued Qyoos and their resolvers. Hosting is replaceable; testnet Qyoos have no monetary value."}</p>
        <nav aria-label="Help"><a href="/developers">Developers</a> · <a href="/privacy">Privacy</a> · <a href="/support">Support</a></nav>
      </footer>
    </>
  );
}

export function QyooImage(props: { qyoo: number; label?: string; class?: string }) {
  return (
    <img
      class={`qyoo-image ${props.class ?? ""}`}
      src={`/v1/qyoo/${props.qyoo}/image.svg`}
      alt={props.label ?? `Qyoo ${props.qyoo}`}
      width="256"
      height="256"
    />
  );
}

export function ExperienceCard(props: { entry: PortfolioEntry; onOpen(): void }) {
  const background = props.entry.experience.background ?? "#171717";
  const foreground = props.entry.experience.foreground ?? "#FFFFFF";
  return (
    <button
      class="experience-card"
      style={{ background, color: foreground }}
      onClick={props.onOpen}
      aria-label={`Open Qyoo ${props.entry.qyoo}`}
    >
      <QyooImage qyoo={props.entry.qyoo} />
      <span class="experience-copy">
        <span class="eyebrow">{props.entry.role === "owner" ? "Owned" : "Controlled"} · #{props.entry.qyoo}</span>
        <strong>{props.entry.experience.title ?? `Qyoo ${props.entry.qyoo}`}</strong>
        <span>{props.entry.experience.subtitle ?? "Ready for an experience"}</span>
      </span>
      <span aria-hidden="true" class="arrow">↗</span>
    </button>
  );
}

export function Notice(props: { kind?: "error" | "success" | "info"; children: ComponentChildren }) {
  return <div class={`notice ${props.kind ?? "info"}`} role={props.kind === "error" ? "alert" : "status"}>{props.children}</div>;
}

export function Spinner(props: { label: string }) {
  return <div class="loading" role="status"><span aria-hidden="true" class="spinner" />{props.label}</div>;
}

export function TechnicalDetails(props: { rows: Array<[string, string | null]> }) {
  return (
    <details class="technical">
      <summary>Technical ownership details</summary>
      <dl>
        {props.rows.map(([name, value]) => (
          <div key={name}><dt>{name}</dt><dd>{value ?? "None"}</dd></div>
        ))}
      </dl>
      <p>Your Qyoo is owned on Ethereum. Qyoo’s hosted resolver is optional and can be replaced.</p>
    </details>
  );
}
